import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { questionsAPI } from "../services/api";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  Radio, RadioGroup, FormControlLabel, FormControl, Checkbox, LinearProgress,
  Chip, Alert, CircularProgress
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import TimerIcon from "@mui/icons-material/Timer";

export default function ExamPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    startExam();
  }, [templateId]);

  useEffect(() => {
    if (!timeLeft) return;
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timer); handleSubmit(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const startExam = async () => {
    try {
      const res = await questionsAPI.startExam(templateId);
      setExam(res.data);
      if (res.data.time_limit_minutes) {
        setTimeLeft(res.data.time_limit_minutes * 60);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start exam.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = exam.questions.map(q => ({
        question_id: q.question_id,
        selected_option_id: Array.isArray(answers[q.question_id])
          ? answers[q.question_id][0] || null  // primary answer
          : answers[q.question_id] || null,
        selected_option_ids: Array.isArray(answers[q.question_id])
          ? answers[q.question_id]
          : answers[q.question_id] ? [answers[q.question_id]] : [],
        time_taken_seconds: 30
      }));
      const res = await questionsAPI.submitExam(exam.attempt_id, formattedAnswers);
      navigate("/results", { state: { result: res.data } });
    } catch {
      setError("Failed to submit exam. Please try again.");
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      <Button variant="contained" onClick={() => navigate("/dashboard")}>
        Back to Dashboard
      </Button>
    </Box>
  );

  const question = exam?.questions[currentQ];
  const progress = ((currentQ + 1) / exam?.questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Assessment in Progress
          </Typography>
          {timeLeft !== null && (
            <Chip
              icon={<TimerIcon />}
              label={formatTime(timeLeft)}
              color={timeLeft < 300 ? "error" : "default"}
              sx={{ mr: 2, color: "white", borderColor: "white" }}
              variant="outlined"
            />
          )}
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {answeredCount} of {exam?.questions.length} answered
          </Typography>
        </Toolbar>
        <LinearProgress variant="determinate" value={progress}
          sx={{ height: 4, bgcolor: "rgba(255,255,255,0.3)",
            "& .MuiLinearProgress-bar": { bgcolor: "#4fc3f7" } }} />
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Question {currentQ + 1} of {exam?.questions.length}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {question?.is_critical && <Chip label="Critical Question" color="error" size="small" />}
            <Chip label={question?.difficulty} size="small"
              color={question?.difficulty === "hard" ? "error" : question?.difficulty === "medium" ? "warning" : "success"} />
            {question?.topic_name && <Chip label={question.topic_name} size="small" variant="outlined" />}
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} mb={2} lineHeight={1.6}>
              {question?.question_text}
            </Typography>
            {question?.image_url && (
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <img src={question.image_url} alt="Question"
                  style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, border: "1px solid #e0e0e0" }} />
              </Box>
            )}

            <FormControl component="fieldset" fullWidth>
              {question?.question_type === "multiple_select" ? (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>Select all that apply</Typography>
                  {question?.answer_options.map((option) => {
                    const selected = Array.isArray(answers[question.question_id])
                      ? answers[question.question_id].includes(option.option_id)
                      : false;
                    return (
                      <Box key={option.option_id} sx={{
                        border: "2px solid",
                        borderColor: selected ? "#1a237e" : "#e0e0e0",
                        borderRadius: 2, mb: 1.5, p: 0.5, cursor: "pointer",
                        bgcolor: selected ? "#e8eaf6" : "white",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "#1a237e", bgcolor: "#f3f4fb" }
                      }}
                        onClick={() => {
                          const current = Array.isArray(answers[question.question_id]) ? answers[question.question_id] : [];
                          const updated = current.includes(option.option_id)
                            ? current.filter(id => id !== option.option_id)
                            : [...current, option.option_id];
                          handleAnswer(question.question_id, updated);
                        }}
                      >
                        <FormControlLabel
                          control={<Checkbox checked={selected} sx={{ color: "#1a237e", "&.Mui-checked": { color: "#1a237e" } }} />}
                          label={<Typography variant="body1">{option.option_text}</Typography>}
                          sx={{ m: 0, width: "100%", p: 1 }}
                        />
                      </Box>
                    );
                  })}
                </>
              ) : (
                <RadioGroup value={answers[question?.question_id] || ""}
                  onChange={(e) => handleAnswer(question.question_id, e.target.value)}>
                  {question?.answer_options.map((option) => (
                    <Box key={option.option_id} sx={{
                      border: "2px solid",
                      borderColor: answers[question.question_id] === option.option_id ? "#1a237e" : "#e0e0e0",
                      borderRadius: 2, mb: 1.5, p: 0.5, cursor: "pointer",
                      bgcolor: answers[question.question_id] === option.option_id ? "#e8eaf6" : "white",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#1a237e", bgcolor: "#f3f4fb" }
                    }}
                      onClick={() => handleAnswer(question.question_id, option.option_id)}
                    >
                      <FormControlLabel
                        value={option.option_id}
                        control={<Radio sx={{ color: "#1a237e", "&.Mui-checked": { color: "#1a237e" } }} />}
                        label={<Typography variant="body1">{option.option_text}</Typography>}
                        sx={{ m: 0, width: "100%", p: 1 }}
                      />
                    </Box>
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="contained" disabled={currentQ === 0}
            onClick={() => setCurrentQ(q => q - 1)}
            style={{ background: currentQ === 0 ? undefined : "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
            {exam?.questions.map((q, i) => (
              <Box key={i} onClick={() => setCurrentQ(i)} sx={{
                width: 32, height: 32, borderRadius: "50%", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, border: "2px solid",
                borderColor: i === currentQ ? "#1a237e" : answers[q.question_id] ? "#4caf50" : "#e0e0e0",
                bgcolor: i === currentQ ? "#1a237e" : answers[q.question_id] ? "#e8f5e9" : "white",
                color: i === currentQ ? "white" : "inherit",
                "&:hover": { borderColor: "#1a237e" }
              }}>
                {i + 1}
              </Box>
            ))}
          </Box>

          {currentQ < exam?.questions.length - 1 ? (
            <Button variant="contained" onClick={() => setCurrentQ(q => q + 1)}
              sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleSubmit}
              disabled={submitting} size="large">
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Submit Exam"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
